/**
 * Soft/hard caps for Must-do bucket (UX guidance only; non-blocking).
 * Used by RideSelector, ShowSelector, RestaurantSelector for badges and inline tips.
 */

import { PARK_KEYS } from "./parkKeys";

export const BADGE_LABELS = {
  mk: "MK",
  epcot: "EPCOT",
  hs: "DHS",
  ak: "AK",
  unscoped: "Unscoped",
};

export const MUST_DO_RECOMMENDED_SOFT_CAPS = {
  mk: 6,
  epcot: 4,
  hs: 5,
  ak: 3,
};

export function getMustDoRecommendedCap(parkKey) {
  return MUST_DO_RECOMMENDED_SOFT_CAPS[parkKey] ?? 0;
}

export function getParksOverMustDoRecommended(mustCountsByPark) {
  return PARK_KEYS.filter((key) => {
    const count = mustCountsByPark[key] ?? 0;
    const recommended = getMustDoRecommendedCap(key);
    return recommended > 0 && count > recommended;
  });
}

export const BUCKET_CAPS = {
  rides: { perParkSoft: 8, perParkHard: 14 },
  shows: { perParkSoft: 3, perParkHard: 6 },
  restaurants: {
    perParkSoft: 2,
    perParkHard: 4,
    unscopedSoft: 2,
    unscopedHard: 4,
  },
};

export function getMustCountsByPark(mustBucket, parkKeys = [...PARK_KEYS, "unscoped"]) {
  const counts = {};
  for (const key of parkKeys) {
    const arr = mustBucket?.[key];
    counts[key] = Array.isArray(arr) ? arr.length : 0;
  }
  return counts;
}

export function getCapStatus(category, mustCountsByPark) {
  const caps = BUCKET_CAPS[category];
  if (!caps) return { overSoft: false, overHard: false, parksOverSoft: [], parksOverHard: [], softCap: 0, hardCap: 0 };

  const parksToCheck = category === "restaurants" ? PARK_KEYS : PARK_KEYS;
  const softCap = caps.perParkSoft ?? 0;
  const hardCap = caps.perParkHard ?? 0;

  const parksOverSoft = [];
  const parksOverHard = [];

  for (const key of parksToCheck) {
    const count = mustCountsByPark[key] ?? 0;
    if (count > hardCap) parksOverHard.push(key);
    else if (count > softCap) parksOverSoft.push(key);
  }

  let unscopedOverSoft = false;
  let unscopedOverHard = false;
  if (category === "restaurants" && caps.unscopedSoft != null) {
    const u = mustCountsByPark.unscoped ?? 0;
    unscopedOverSoft = u > (caps.unscopedSoft ?? 0);
    unscopedOverHard = u > (caps.unscopedHard ?? 0);
  }

  const overSoft = parksOverSoft.length > 0 || unscopedOverSoft;
  const overHard = parksOverHard.length > 0 || unscopedOverHard;

  return {
    overSoft,
    overHard,
    parksOverSoft,
    parksOverHard,
    softCap,
    hardCap,
    unscopedOverSoft: category === "restaurants" ? unscopedOverSoft : undefined,
    unscopedOverHard: category === "restaurants" ? unscopedOverHard : undefined,
  };
}

export function getRidesCapMessage(capStatus) {
  if (!capStatus?.overSoft && !capStatus?.overHard) return null;
  const n = capStatus.softCap;
  if (capStatus.overHard) {
    return `Tip: Most itineraries work best with around ${n} must-do rides per park day. Consider moving some to Nice-to-have for a more relaxed day.`;
  }
  return `Tip: Most itineraries work best with ~${n} must-do rides per park day. Consider moving some to Nice-to-have.`;
}

export function getShowsCapMessage(capStatus) {
  if (!capStatus?.overSoft && !capStatus?.overHard) return null;
  const n = capStatus.softCap;
  if (capStatus.overHard) {
    return `Tip: Picking around ${n} must-see shows per park usually works best. Consider moving some to Nice-to-have.`;
  }
  return `Tip: Most plans work well with ~${n} must-see shows per park. Consider moving some to Nice-to-have.`;
}

export function getRestaurantsCapMessage(capStatus) {
  if (!capStatus?.overSoft && !capStatus?.overHard) return null;
  const n = capStatus.softCap;
  if (capStatus.overHard) {
    return `Tip: Around ${n} must-try restaurants per park (and per non-park area) usually works best. Consider moving some to Nice-to-have.`;
  }
  return `Tip: Most itineraries work best with ~${n} must-try restaurants per park. Consider moving some to Nice-to-have.`;
}
